/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import { api } from '../../redux/api';
import { getCookie } from '../../hookServices/cookiesManager';
import { plugTypeMaping } from '../../utils/utils';
import {
  createPortStickerPreviewDataUrl,
  downloadPortStickerPng,
  START_PAGE_STICKER_PRINT_ORDER_URL,
} from './startPagePortStickerPng';
import { shareViaApps } from '../../utils/shareViaApps';

export function buildStartPageShareUrl(stationNumber) {
  const station = String(stationNumber ?? '').trim();
  if (typeof window === 'undefined') {
    return station ? `/start?station=${encodeURIComponent(station)}` : '/start';
  }
  const url = new URL('/start', window.location.origin);
  if (station) {
    url.searchParams.set('station', station);
  }
  return url.toString();
}

function useShareStation(stationNumber) {
  const position = getCookie('userLocation');
  const lat = position?.latitude != null ? position.latitude : '';
  const lon = position?.longitude != null ? position.longitude : '';
  return api.useGetDeviceStationQuery(
    { stationNumber, lat, lon },
    { skip: !stationNumber }
  );
}

export default function StartPageShareButton({ stationNumber, className = 'layout-page-chrome-btn' }) {
  const { t } = useTranslation();
  const [qrOpen, setQrOpen] = useState(false);
  const [stickerPreviewUrl, setStickerPreviewUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [downloadingSticker, setDownloadingSticker] = useState(false);
  const [stickerError, setStickerError] = useState(false);

  const { data: station } = useShareStation(stationNumber);

  const shareUrl = useMemo(() => buildStartPageShareUrl(stationNumber), [stationNumber]);
  const titleId = 'start-page-share-qr';
  const portTitle = `${t('startPageShareStickerPortLabel')}${stationNumber}`;

  const plugTypeLabel = plugTypeMaping[station?.plugType] || '';
  const maxPowerKw = station?.maxPowerWt > 0 ? Math.round(station.maxPowerWt / 1000) : null;
  const portTypeLine = useMemo(() => {
    if (!plugTypeLabel && maxPowerKw == null) return '';
    if (plugTypeLabel && maxPowerKw != null) {
      return t('startPageSharePortTypeWithPower', { type: plugTypeLabel, kw: maxPowerKw });
    }
    if (plugTypeLabel) return t('startPageSharePortTypeOnly', { type: plugTypeLabel });
    return t('startPageSharePortPowerOnly', { kw: maxPowerKw });
  }, [plugTypeLabel, maxPowerKw, t]);

  const stickerParams = useMemo(
    () => ({
      stationNumber,
      shareUrl,
      portTitle,
    }),
    [stationNumber, shareUrl, portTitle]
  );

  useEffect(() => {
    if (!qrOpen || !shareUrl || !stationNumber) {
      setStickerPreviewUrl('');
      return undefined;
    }

    let active = true;
    createPortStickerPreviewDataUrl(stickerParams)
      .then(url => {
        if (active) setStickerPreviewUrl(url);
      })
      .catch(() => {
        if (active) setStickerPreviewUrl('');
      });

    return () => {
      active = false;
    };
  }, [qrOpen, shareUrl, stationNumber, stickerParams]);

  useEffect(() => {
    if (!qrOpen) {
      setCopied(false);
    }
  }, [qrOpen]);

  const copyLink = async () => {
    setCopied(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        return;
      }
    } catch {
      // Fall through to a hidden textarea copy for older webviews.
    }
    try {
      const input = document.createElement('textarea');
      input.value = shareUrl;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    } catch {
      // Keep the copied message visible even if the environment blocks clipboard APIs.
    }
  };

  const handleDownloadSticker = async () => {
    if (downloadingSticker) return;
    setStickerError(false);
    setDownloadingSticker(true);
    try {
      await downloadPortStickerPng(stickerParams);
      ReactGA.event({
        category: 'share',
        action: 'start page sticker png download',
        label: String(stationNumber),
      });
    } catch {
      setStickerError(true);
    } finally {
      setDownloadingSticker(false);
    }
  };

  const openShareFallback = () => {
    ReactGA.event({
      category: 'share',
      action: 'start page share modal',
      label: String(stationNumber ?? ''),
    });
    setQrOpen(true);
  };

  const handleShareClick = () => {
    void copyLink();
    openShareFallback();
  };

  const handleShareViaApps = () => {
    ReactGA.event({
      category: 'share',
      action: 'start page share via apps',
      label: String(stationNumber ?? ''),
    });
    void shareViaApps({
      title: t('startPageShareTitle', { port: stationNumber }),
      text: t('startPageShareText', { port: stationNumber }),
      url: shareUrl,
    });
  };

  if (!stationNumber) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={t('startPageShareBtn')}
        onClick={handleShareClick}
      >
        <ShareOutlinedIcon className="layout-page-chrome-btn__icon" aria-hidden />
      </button>

      <Modal modalOpen={qrOpen} setModalOpen={setQrOpen} scrollable>
        <div className="start-page-share-qr-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <h2 id={titleId} className="start-page-share-qr-sr-title">
            {portTitle}
          </h2>
          {portTypeLine ? <p className="start-page-share-qr-port-type">{portTypeLine}</p> : null}
          {stickerPreviewUrl ? (
            <button
              type="button"
              className="start-page-share-qr-copy-hit"
              onClick={copyLink}
              aria-label={copied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            >
              <img
                src={stickerPreviewUrl}
                alt={t('startPageShareStickerPreviewAlt', { port: stationNumber })}
                className="start-page-share-sticker-preview"
              />
            </button>
          ) : (
            <div className="start-page-share-sticker-preview start-page-share-sticker-preview--loading" aria-hidden />
          )}
          <button type="button" className="start-page-share-qr-url" onClick={copyLink}>
            {shareUrl}
          </button>
          {copied ? (
            <p className="start-page-share-qr-copied" role="status">
              {t('b2bMarketplaceShareCopied')}
            </p>
          ) : null}
          <div className="start-page-share-qr-actions">
            <button
              type="button"
              className="b2b-btn b2b-btn--outline start-page-share-qr-download-btn"
              onClick={handleShareViaApps}
            >
              {t('b2bMarketplaceShareBtn')}
            </button>
            <button
              type="button"
              className="b2b-btn b2b-btn--outline start-page-share-qr-download-btn"
              disabled={downloadingSticker}
              onClick={() => void handleDownloadSticker()}
            >
              {downloadingSticker ? t('startPageShareStickerDownloading') : t('startPageShareStickerDownload')}
            </button>
          </div>
          {stickerError ? (
            <p className="start-page-share-qr-error" role="alert">
              {t('startPageShareStickerError')}
            </p>
          ) : null}
          <p className="start-page-share-qr-print-order">
            <a href={START_PAGE_STICKER_PRINT_ORDER_URL} target="_blank" rel="noopener noreferrer">
              {t('startPageSharePrintOrder')}
            </a>
          </p>
        </div>
      </Modal>
    </>
  );
}
